import { create } from 'zustand';
import { User } from '../types';
import { authApi } from '../services/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isCheckingAuth: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; full_name: string }) => Promise<void>;
  googleLogin: (credential: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  updateUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  isCheckingAuth: false,

  login: async (email, password) => {
    set({ isLoading: true });
    try {
      const { data } = await authApi.login(email, password);
      localStorage.setItem('token', data.access_token);
      set({
        user: data.user,
        token: data.access_token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  register: async (data) => {
    set({ isLoading: true });
    try {
      await authApi.register(data);
      // Auto login after registration
      await get().login(data.email, data.password);
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  googleLogin: async (credential) => {
    set({ isLoading: true });
    try {
      const { data } = await authApi.googleAuth(credential);
      localStorage.setItem('token', data.access_token);
      set({
        user: data.user,
        token: data.access_token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    set({ user: null, token: null, isAuthenticated: false });
  },

  updateUser: (user) => {
    set({ user });
  },

  checkAuth: async () => {
    const { token, user } = get();
    if (!token) {
      set({ isCheckingAuth: false });
      return;
    }
    // Already have the user (e.g. just logged in) — don't gate the whole app
    // behind a full-screen "Loading…". Revalidate quietly in the background;
    // a stale/expired token is handled by the axios 401 interceptor.
    if (user) {
      set({ isAuthenticated: true, isCheckingAuth: false });
      authApi.getMe()
        .then(({ data }) => set({ user: data, isAuthenticated: true }))
        .catch(() => { /* keep the cached session */ });
      return;
    }
    // Cold load / hard refresh: token present but no user yet — block until we
    // know whether the token is valid.
    set({ isCheckingAuth: true });
    try {
      const { data } = await authApi.getMe();
      set({ user: data, isAuthenticated: true, isCheckingAuth: false });
    } catch {
      localStorage.removeItem('token');
      set({ user: null, token: null, isAuthenticated: false, isCheckingAuth: false });
    }
  },
}));
